import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";

const when = (d) => (d ? new Date(d).toLocaleString() : "");

// Coordinator ↔ school requester conversation attached to one event.
export const EventThread = ({ event, onClose, onSent }) => {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const open = !!event;

  useEffect(() => {
    if (!event) return;
    api.get(`/engagement-events/${event.id}/messages`).then(({ data }) => setMsgs(data)).catch(() => setMsgs([]));
    // Opening the thread counts as reading it — stops the banner flashing for this event.
    api.post("/notifications/read-messages", { link: `/engagement?event=${event.id}` })
      .then(() => window.dispatchEvent(new Event("cc:notifs-changed")))
      .catch(() => {});
  }, [event]);

  const send = async () => {
    if (!text.trim()) return toast.error("Write a message first");
    setSending(true);
    try {
      const { data } = await api.post(`/engagement-events/${event.id}/messages`, { message: text });
      setMsgs((m) => [...m, data.message]);
      setText("");
      toast.success(data.emailed ? `Sent — ${data.emailed} person(s) notified` : "Sent");
      onSent?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Messages — {event?.title}</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-500">Ask about dates, audience or logistics before scheduling. Everyone gets it in-app plus one email nudge.</p>
        <div className="max-h-64 overflow-y-auto cc-scroll space-y-2 my-2" data-testid="event-thread-list">
          {msgs.length === 0 ? <p className="text-sm text-zinc-400">No messages yet.</p> :
            msgs.map((m) => (
              <div key={m.id} className="border rounded-md px-3 py-2" data-testid={`event-msg-${m.id}`}>
                <p className="text-xs text-zinc-400">{m.from_name} · {m.from_role} · {when(m.created_at)}</p>
                <p className="text-sm text-zinc-800 whitespace-pre-wrap">{m.message}</p>
              </div>
            ))}
        </div>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Type your message…" data-testid="event-msg-input" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="event-thread-close">Close</Button>
          <Button onClick={send} disabled={sending} className="bg-cc-blue text-white" data-testid="event-msg-send">
            {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
